import { Flash } from '@carbon/icons-react';
import * as React from 'react';
import { NavbarDropdownItem, NavbarSchema } from 'vet-bones/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import { NAVBAR_PORTAL_ENDPOINT_BUTTON } from 'src/ui/constants/testIds';
import { useLaunchpadNavbarAvatarSchema } from 'src/ui/containers/launchpad/navbar/avatar';
import { NavbarAsideType, toggledNavbarAside } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useSavedConnectionsQuery } from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import {
  useCurrentConnectionIdx,
  useLandingConnectionIdx,
  useOrgDomain,
} from 'src/ui/hooks/navbar/connection';
import {
  useKnowledgeCatalogNavbarDropdownItem,
  usePortalNavbarDropdownItem,
  useVoiceboxNavbarDropdownItem,
} from 'src/ui/hooks/navbar/dropdown';
import { useNavbarExpandSchema } from 'src/ui/hooks/navbar/expand';
import { useNavbarLearnStardogSchema } from 'src/ui/hooks/navbar/learnStardog';
import { useNavbarSduDashboardSchema } from 'src/ui/hooks/navbar/sduDashboard';
import { useNavbarSupport } from 'src/ui/hooks/navbar/support';
import { useVoiceboxNavbarItems } from 'src/ui/hooks/navbar/voicebox';
import * as copy from 'src/ui/templates/copy';

export const useLaunchpadLandingNavbarSchema = (): NavbarSchema => {
  const dispatch = useAppDispatch();

  const org = useOrgDomain();
  const connectionIdx = useLandingConnectionIdx();
  const dropdownSelectedItem = usePortalNavbarDropdownItem(connectionIdx);
  const voiceboxItem = useVoiceboxNavbarDropdownItem(connectionIdx);
  const knowledgeCatalogItem = useKnowledgeCatalogNavbarDropdownItem(
    connectionIdx
  );
  const { isKnowledgeCatalogSupported, isVoiceboxSupported } = useNavbarSupport(
    connectionIdx
  );
  const { connection } = useCurrentConnection(connectionIdx, org);

  const dropdownItems = React.useMemo(() => {
    const items: NavbarDropdownItem[] = [];
    if (isVoiceboxSupported) {
      items.push(voiceboxItem);
    }
    if (isKnowledgeCatalogSupported) {
      items.push(knowledgeCatalogItem);
    }
    return items;
  }, [
    isKnowledgeCatalogSupported,
    isVoiceboxSupported,
    knowledgeCatalogItem,
    voiceboxItem,
  ]);

  const { data: savedConnectionsData } = useSavedConnectionsQuery(
    graphQLClient,
    {}
  );
  const hasEndpoints = savedConnectionsData?.listConnections?.length;

  const isEndpointAsideOpen = useAppSelector(
    (state) =>
      state.ui.navbar.aside === NavbarAsideType.PORTAL_ENDPOINT &&
      state.ui.navbar.isAsideOpen
  );
  const handlePortalEndpointAside = React.useCallback(() => {
    dispatch(
      toggledNavbarAside({
        aside: NavbarAsideType.PORTAL_ENDPOINT,
        isAsideOpen: !isEndpointAsideOpen,
      })
    );
  }, [dispatch, isEndpointAsideOpen]);

  const expand = useNavbarExpandSchema();
  const learnStardog = useNavbarLearnStardogSchema(true);
  const sduDashboard = useNavbarSduDashboardSchema();
  const avatar = useLaunchpadNavbarAvatarSchema();

  return React.useMemo(
    () => ({
      dropdown: {
        items: dropdownItems,
        selectedItem: dropdownSelectedItem,
      },
      expand,
      secondaryItems: hasEndpoints
        ? [
            {
              active: isEndpointAsideOpen,
              icon: Flash,
              label:
                connection?.name || copy.components.navbar.landing.endpoint,
              onClick: handlePortalEndpointAside,
              testId: NAVBAR_PORTAL_ENDPOINT_BUTTON,
            },
          ]
        : [],
      tertiaryItems: [learnStardog, ...sduDashboard],
      footer: { avatar },
    }),
    [
      avatar,
      connection,
      dropdownItems,
      dropdownSelectedItem,
      expand,
      handlePortalEndpointAside,
      hasEndpoints,
      isEndpointAsideOpen,
      learnStardog,
      sduDashboard,
    ]
  );
};

export const useLaunchpadDefaultNavbarSchema = (): NavbarSchema => {
  const connectionIdx = useCurrentConnectionIdx();
  const dropdownSelectedItem = usePortalNavbarDropdownItem(connectionIdx);

  const expand = useNavbarExpandSchema();
  const learnStardog = useNavbarLearnStardogSchema(true);
  const sduDashboard = useNavbarSduDashboardSchema();
  const avatar = useLaunchpadNavbarAvatarSchema();

  return React.useMemo(
    () => ({
      dropdown: {
        items: [],
        selectedItem: dropdownSelectedItem,
      },
      expand,
      tertiaryItems: [learnStardog, ...sduDashboard],
      footer: { avatar },
    }),
    [avatar, dropdownSelectedItem, expand, learnStardog, sduDashboard]
  );
};

export const useLaunchpadVoiceboxNavbarSchema = (): NavbarSchema => {
  const connectionIdx = useCurrentConnectionIdx();
  const dropdownSelectedItem = useVoiceboxNavbarDropdownItem(connectionIdx);
  const portalItem = usePortalNavbarDropdownItem(connectionIdx);
  const knowledgeCatalogItem = useKnowledgeCatalogNavbarDropdownItem(
    connectionIdx
  );
  const { isKnowledgeCatalogSupported } = useNavbarSupport(connectionIdx);

  const dropdownItems = React.useMemo(() => {
    const items: NavbarDropdownItem[] = [portalItem];
    if (isKnowledgeCatalogSupported) {
      items.push(knowledgeCatalogItem);
    }
    return items;
  }, [isKnowledgeCatalogSupported, knowledgeCatalogItem, portalItem]);

  const secondaryItems = useVoiceboxNavbarItems();

  const expand = useNavbarExpandSchema();
  const learnStardog = useNavbarLearnStardogSchema(true);
  const sduDashboard = useNavbarSduDashboardSchema();
  const avatar = useLaunchpadNavbarAvatarSchema();

  return React.useMemo(
    () => ({
      dropdown: {
        items: dropdownItems,
        selectedItem: dropdownSelectedItem,
      },
      expand,
      secondaryItems,
      tertiaryItems: [learnStardog, ...sduDashboard],
      footer: { avatar },
    }),
    [
      avatar,
      dropdownItems,
      dropdownSelectedItem,
      expand,
      learnStardog,
      sduDashboard,
      secondaryItems,
    ]
  );
};
